import { Injectable, inject } from '@angular/core';
import { Observable, tap, catchError, finalize } from 'rxjs';
import { StudentAssignmentApiService } from './student-assignment-api.service';
import { StudentAssignmentStateService } from './student-assignment-state.service';
import {
  AssignmentPreview,
  GetSubmissionResponse,
  SubmissionResponse,
  SaveAnswerDto,
  StudentAssignmentStep,
  StudentAssignmentErrorType,
} from './student-assignment.models';

@Injectable({
  providedIn: 'root',
})
export class StudentSubmissionService {
  private readonly apiService = inject(StudentAssignmentApiService);
  private readonly state = inject(StudentAssignmentStateService);

  // ============================================
  // PREVIEW (PUBLIC - NO AUTH)
  // ============================================

  loadPreview(shareCode: string): Observable<AssignmentPreview> {
    this.state.setShareCode(shareCode);
    this.state.setIsLoading(true);
    this.state.setError(null);
    this.state.setErrorType(null);

    return this.apiService.getPreview(shareCode).pipe(
      tap((preview) => {
        this.state.setPreview(preview);

        // Check if assignment is expired
        if (preview.dueDate && new Date(preview.dueDate) < new Date()) {
          this.state.setErrorType(StudentAssignmentErrorType.EXPIRED);
          this.state.setError(
            'This assignment has passed its due date.',
          );
          this.state.setStep(StudentAssignmentStep.ERROR);
        }
      }),
      catchError((err) => {
        if (err.status === 404) {
          this.state.setErrorType(StudentAssignmentErrorType.NOT_FOUND);
          this.state.setError('Assignment not found.');
        } else {
          this.state.setErrorType(StudentAssignmentErrorType.UNKNOWN);
          this.state.setError('Failed to load assignment.');
        }
        this.state.setStep(StudentAssignmentStep.ERROR);
        throw err;
      }),
      finalize(() => this.state.setIsLoading(false)),
    );
  }

  // ============================================
  // START SUBMISSION (REQUIRES AUTH)
  // ============================================

  startSubmission(): Observable<SubmissionResponse> {
    const shareCode = this.state.shareCode;
    if (!shareCode) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No share code'));
      });
    }

    this.state.setIsLoading(true);
    this.state.setError(null);

    return this.apiService.startSubmission(shareCode).pipe(
      tap((submission) => {
        this.handleSubmissionLoaded(submission);
        this.state.setStep(StudentAssignmentStep.QUESTIONS);
      }),
      catchError((err) => {
        this.handleSubmissionError(err);
        throw err;
      }),
      finalize(() => this.state.setIsLoading(false)),
    );
  }

  // ============================================
  // LOAD EXISTING SUBMISSION
  // ============================================

  loadSubmission(): Observable<GetSubmissionResponse> {
    const shareCode = this.state.shareCode;
    if (!shareCode) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No share code'));
      });
    }

    this.state.setIsLoading(true);
    this.state.setError(null);

    return this.apiService.getSubmission(shareCode).pipe(
      tap((response) => {
        if (response.started && response.submission) {
          this.handleSubmissionLoaded(response.submission);
        }
      }),
      catchError((err) => {
        this.handleSubmissionError(err);
        throw err;
      }),
      finalize(() => this.state.setIsLoading(false)),
    );
  }

  // ============================================
  // HELPERS
  // ============================================

  handleSubmissionLoaded(submission: SubmissionResponse): void {
    this.state.setSubmission(submission);

    // Set expiration timer
    if (submission.expiresAt) {
      this.state.setExpiresAt(new Date(submission.expiresAt));
    }

    // Restore local answers from submission
    const localAnswers = new Map<string, SaveAnswerDto>();
    submission.answers.forEach((a) => {
      localAnswers.set(a.questionId, {
        selectedOptionId: a.selectedOptionId,
        textAnswer: a.textAnswer,
      });
    });
    this.state.setLocalAnswers(localAnswers);

    // Resume at last viewed question
    this.resumeAtLastViewedQuestion(submission);
  }

  private resumeAtLastViewedQuestion(
    submission: SubmissionResponse,
  ): void {
    // Find the question with the most recent lastViewedAt
    const answersWithViewTime = submission.answers
      .filter((a) => a.lastViewedAt)
      .sort(
        (a, b) =>
          new Date(b.lastViewedAt!).getTime() -
          new Date(a.lastViewedAt!).getTime(),
      );

    let targetIndex = 0;
    if (answersWithViewTime.length > 0) {
      const lastViewedQuestionId = answersWithViewTime[0].questionId;
      const index = submission.questions.findIndex(
        (q) => q.id === lastViewedQuestionId,
      );
      targetIndex = Math.max(0, index);
    }

    this.state.setCurrentQuestionIndex(targetIndex);
  }

  handleSubmissionError(err: any): void {
    if (err.status === 400) {
      const message = err.error?.message || '';
      if (message.includes('expired')) {
        this.state.setErrorType(StudentAssignmentErrorType.EXPIRED);
        this.state.setError('Your submission has expired.');
      } else if (message.includes('already submitted')) {
        this.state.setErrorType(
          StudentAssignmentErrorType.ALREADY_SUBMITTED,
        );
        this.state.setError(
          'You have already submitted this assignment.',
        );
      } else if (message.includes('due date')) {
        this.state.setErrorType(StudentAssignmentErrorType.EXPIRED);
        this.state.setError(
          'This assignment has passed its due date.',
        );
      } else {
        this.state.setErrorType(StudentAssignmentErrorType.UNKNOWN);
        this.state.setError(
          message || 'Failed to start assignment.',
        );
      }
    } else {
      this.state.setErrorType(StudentAssignmentErrorType.UNKNOWN);
      this.state.setError(
        'Failed to start assignment. Please try again.',
      );
    }
    this.state.setStep(StudentAssignmentStep.ERROR);
  }
}
